import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Button } from '@domcrm/embed-ui';

import { postAuthLogin2FA, postStaffLogin } from '../widget-api/auth-staff-login';
import {
  isContextRequired,
  postAuthSelectContext,
  type ContextRequiredResponse,
  type LoginContextOption
} from '../widget-api/auth-contexts';
import { getMe, postPublicResendEmailVerification } from '../widget-api/auth-me';
import { ApiError } from '../widget-api/http-client';
import { useEmbedBootstrap } from '../use-widget-bootstrap';
import { useQuerySuffix } from '../widget-hooks/useQuerySuffix';
import { useWidgetSeo } from '../widget-hooks/use-widget-seo';
import { isTrainerSurfaceMe } from '../widget-lib/embed-route-policy';
import { trainerOrgMatchesEmbed } from '../widget-lib/embed-trainer-org';

import { getCallStatus, postRequestCall } from '../widget-api/auth-call';
import { normalizePhoneRu, validatePhoneRu } from '../widget-lib/phone';
import { telHref } from '../widget-lib/phone-tel';
import { validateEmail } from '../widget-lib/validation';
import { EmbedPhoneInput } from '../widget-ui/embed-phone-input';
import { EmbedMessageBlock, embedFieldErrorInputClass } from '../widget-ui/embed-message-block';
import { ensureOrgSlugInSearchSuffix } from '../widget-lib/auth-search';
import { setEmbedSession, setGlobalEmbedSession } from '../widget-lib/embed-session-store';
import { clearPendingCall, loadPendingCall, savePendingCall } from '../widget-lib/pending-call';
import { PATH } from '../widget-lib/path-routes';
import { useSmartCaptcha } from '../widget-lib/smartcaptcha';
import { SmartCaptchaNotice } from '../widget-ui/smart-captcha-notice';
import { ContextSelectScreen } from '../widget-ui/context-select-screen';

/** Текст ошибки входа в тренерский раздел: без технических слов, с подсказкой про клиента. */
function trainerLoginErrorMessage(err: ApiError): string {
  if (err.errorType === 'no_staff_role') {
    return 'У вас нет роли сотрудника. Если вы клиент — войдите в приложение студии';
  }
  if (err.errorType === 'no_role_in_organization') {
    return 'У вас нет роли в этой студии';
  }
  if (err.errorType === 'client_self_join_staff_only') {
    return 'Студия принимает новых клиентов только через администратора';
  }
  return err.detail;
}

/** Резервный код 2FA: буквы/цифры, формат XXXXX-XXXXX (дефис ставим сами). */
function normalizeRecoveryCodeInput(raw: string): string {
  const clean = raw.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10);
  return clean.length > 5 ? `${clean.slice(0, 5)}-${clean.slice(5)}` : clean;
}

const inputClass =
  'min-h-10 w-full rounded-sm border-0 bg-[var(--bg-soft)] px-3 py-2 text-base text-[var(--text)] focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)] sm:min-h-8 sm:py-1 sm:text-[13px]';

const CALL_RESEND_COOLDOWN_S = 30;

/** Кулдаун повторного письма-верификации; backend rate-limit — не более 10 в час с одного IP. */
const RESEND_COOLDOWN_S = 300;

function IconEye() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function IconEyeOff() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
      <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
      <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
      <line x1="2" x2="22" y1="2" y2="22" />
    </svg>
  );
}

export default function TrainerSignInPage() {
  useWidgetSeo(
    'Вход для тренера',
    'Вход в рабочий кабинет тренера и инструктора.'
  );
  const navigate = useNavigate();
  const suffix = useQuerySuffix();
  const [searchParams] = useSearchParams();
  const { orgSlug, widgetConfig } = useEmbedBootstrap() ?? { orgSlug: null, widgetConfig: null };
  const effectiveOrgSlug = orgSlug ?? null;
  const [formError, setFormError] = useState<string | null>(null);

  // Auth tabs: call / password
  const [authTab, setAuthTab] = useState<'call' | 'password'>('call');
  const [callStep, setCallStep] = useState<'phone' | 'callcheck'>('phone');

  // Call flow state
  const [callPhoneInput, setCallPhoneInput] = useState('+7 ');
  const [checkId, setCheckId] = useState<string | null>(null);
  const [callSubmitting, setCallSubmitting] = useState(false);
  const [callCooldown, setCallCooldown] = useState(0);
  const lastPhoneNormRef = useRef<string>('');
  const [callcheckPhonePretty, setCallcheckPhonePretty] = useState('');

  // Восстанавливаем ожидание звонка после возврата из телефонного приложения/перезагрузки WebView.
  useEffect(() => {
    const pending = loadPendingCall('trainer', effectiveOrgSlug);
    if (!pending) return;
    setCallPhoneInput(pending.userPhone || '+7 ');
    setCallcheckPhonePretty(pending.callPhonePretty || '');
    setCallStep('callcheck');
    setCheckId(pending.checkId);
  }, [effectiveOrgSlug]);

  // Password flow state
  const [loginEmail, setLoginEmail] = useState('');
  const [accountPhone, setAccountPhone] = useState('+7 ');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  /** Гейт email: показываем форму повторной отправки письма-верификации. */
  const [emailNotVerified, setEmailNotVerified] = useState(false);
  const [resendBusy, setResendBusy] = useState(false);
  const [resendNote, setResendNote] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [fieldErrors, setFieldErrors] = useState<{ email?: string; phone?: string; password?: string }>({});

  // Второй шаг входа: включённая 2FA (пароль или звонок).
  const [twoFARequired, setTwoFARequired] = useState(false);
  const [tempToken, setTempToken] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [totpRecoveryMode, setTotpRecoveryMode] = useState(false);
  const [totpError, setTotpError] = useState<string | null>(null);
  const [totpLoading, setTotpLoading] = useState(false);

  // Выбор раздела после подтверждения личности (несколько ролей/студий).
  const [contextChoice, setContextChoice] = useState<ContextRequiredResponse | null>(null);
  const [contextBusyId, setContextBusyId] = useState<string | null>(null);
  const [contextError, setContextError] = useState<string | null>(null);

  // Yandex SmartCaptcha (невидимая): включается бэкендом (SMARTCAPTCHA_MODE=required).
  const captcha = useSmartCaptcha();

  /** Общий финиш входа staff: проверка контекста/организации и переход в кабинет тренера. */
  const completeStaffLogin = useCallback(async (tokens: {
    access_token: string;
    refresh_token?: string | null;
  }): Promise<boolean> => {
    const me = await getMe(tokens.access_token);
    // Доступ в раздел тренера — по контексту токена; старые сессии без контекста — по роли.
    if (!isTrainerSurfaceMe(me)) {
      setFormError('В этом приложении доступен раздел тренера. Если вы клиент — войдите в приложение студии.');
      return false;
    }
    if (effectiveOrgSlug && !trainerOrgMatchesEmbed(me, effectiveOrgSlug, widgetConfig)) {
      setFormError('Эта ссылка относится к другой организации, чем ваш текущий вход.');
      return false;
    }
    const oid = me.organization_id;
    const isPlatformContext =
      me.context?.surface === 'trainer' &&
      me.context.org_id === 0 &&
      (me.role === 'superadmin' || me.role === 'platform_staff');
    if (oid == null && !isPlatformContext) {
      setFormError('Нет контекста организации. Обратитесь к администратору.');
      return false;
    }
    const effectiveSlug =
      effectiveOrgSlug ||
      me.memberships?.find((m) => m.organization_id === oid)?.organization_slug ||
      `org_${oid}`;
    const normalizedSuffix = suffix.startsWith('?') ? suffix : `?${suffix}`;
    const sessionPayload = {
      accessToken: tokens.access_token,
      organizationId: oid,
      embedMode: 'trainer' as const,
      refreshToken: tokens.refresh_token ?? undefined
    };
    if (oid == null) {
      // Платформенный контекст: сначала выбор студии, сессия — глобальная.
      setGlobalEmbedSession(sessionPayload);
      navigate(`${PATH.trainerSelectStudio}${normalizedSuffix}`, { replace: true });
      return true;
    }
    const finalSuffix = ensureOrgSlugInSearchSuffix(normalizedSuffix, effectiveSlug);
    setEmbedSession(effectiveSlug, sessionPayload);
    navigate(`${PATH.trainer}${finalSuffix}`);
    return true;
  }, [navigate, suffix, effectiveOrgSlug, widgetConfig]);

  const showContextChoice = useCallback((res: ContextRequiredResponse) => {
    setContextChoice(res);
    setContextError(null);
    setContextBusyId(null);
    setTwoFARequired(false);
    setTempToken('');
    setTotpCode('');
    setTotpRecoveryMode(false);
    setTotpError(null);
    setFormError(null);
  }, []);

  const onSelectContext = useCallback(async (context: LoginContextOption) => {
    const choice = contextChoice;
    if (!choice) return;
    setContextBusyId(context.id);
    setContextError(null);
    try {
      const tokens = await postAuthSelectContext(choice.selection_token, context.id);
      setContextChoice(null);
      await completeStaffLogin(tokens);
    } catch (e) {
      if (e instanceof ApiError) {
        if (e.errorType === 'client_self_join_staff_only') {
          setContextError('Студия принимает новых клиентов только через администратора');
        } else if (e.errorType === 'selection_token_invalid' || e.status === 401) {
          setContextError('Время выбора истекло. Войдите заново.');
          setContextChoice(null);
        } else {
          setContextError(e.detail);
        }
      } else {
        setContextError(e instanceof Error ? e.message : 'Не удалось войти. Попробуйте ещё раз.');
      }
    } finally {
      setContextBusyId(null);
    }
  }, [completeStaffLogin, contextChoice]);

  const canSubmitCall = useMemo(() => {
    const phoneOk = validatePhoneRu(callPhoneInput, { required: true }) === true;
    return phoneOk && !callSubmitting;
  }, [callPhoneInput, callSubmitting]);

  useEffect(() => {
    if (callCooldown <= 0) return;
    const timer = setInterval(() => {
      setCallCooldown((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [callCooldown]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setInterval(() => {
      setResendCooldown((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  // Poll call status
  useEffect(() => {
    if (!checkId) return;

    let stopped = false;
    let intervalId: ReturnType<typeof setInterval> | null = null;

    const stopPoll = () => {
      if (intervalId != null) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    const finish = () => {
      stopPoll();
      setCheckId(null);
      clearPendingCall('trainer');
      setCallStep('phone');
    };

    const tick = async () => {
      try {
        const s = await getCallStatus(checkId);
        if (stopped) return;

        if (s.status === 'pending') {
          return;
        }

        if (s.status === 'expired') {
          finish();
          setFormError('Время попытки авторизации истекло. Запросите вход заново.');
          return;
        }

        if (s.status === 'two_factor_required') {
          stopPoll();
          setCheckId(null);
          clearPendingCall('trainer');
          setCallStep('phone');
          setTempToken(s.temp_token ?? '');
          setTwoFARequired(true);
          setTotpRecoveryMode(false);
          setTotpCode('');
          setTotpError(null);
          setFormError(null);
          return;
        }

        if (s.status === 'context_required') {
          stopPoll();
          setCheckId(null);
          clearPendingCall('trainer');
          setCallStep('phone');
          showContextChoice({
            status: 'context_required',
            contexts: s.contexts ?? [],
            selection_token: s.selection_token ?? '',
            expires_in: s.expires_in
          });
          return;
        }

        if (s.status === 'confirmed') {
          stopPoll();
          setCheckId(null);
          clearPendingCall('trainer');

          if (s.access_token) {
            const ok = await completeStaffLogin({
              access_token: s.access_token,
              refresh_token: s.refresh_token ?? undefined
            });
            if (!ok) setCallStep('phone');
            return;
          }

          if (s.registration_token) {
            finish();
            setFormError('Эта учётная запись не привязана к сотруднику. Обратитесь к администратору организации.');
            return;
          }

          finish();
          setFormError('Не удалось завершить вход по коду.');
          return;
        }

        finish();
        setFormError('Не удалось завершить вход. Попробуйте снова или войдите по паролю.');
      } catch (e) {
        if (stopped) return;
        if (e instanceof ApiError) {
          if (e.status === 401) {
            finish();
            setFormError('Пользователь с данными аккаунта не найден. Обратитесь к администратору.');
            return;
          }
          if (e.status === 404) {
            finish();
            setFormError('Сессия проверки недоступна. Запросите код снова.');
            return;
          }
          if (e.status === 403) {
            finish();
            setFormError(trainerLoginErrorMessage(e));
            return;
          }
        }
        finish();
        setFormError(e instanceof Error ? e.message : 'Ошибка входа');
      }
    };

    intervalId = setInterval(() => {
      void tick();
    }, 1500);
    void tick();

    return () => {
      stopped = true;
      stopPoll();
    };
  }, [checkId, navigate, suffix, effectiveOrgSlug, widgetConfig, completeStaffLogin, showContextChoice]);

  const switchTab = (tab: 'call' | 'password') => {
    setAuthTab(tab);
    setFormError(null);
    if (tab === 'call') {
      setCallStep('phone');
    }
  };

  const submitRequestCall = async (phoneNorm: string) => {
    setCallSubmitting(true);
    try {
      const res = await captcha.run((captchaToken) =>
        postRequestCall({
          phone: phoneNorm,
          org_slug: effectiveOrgSlug,
          ...(effectiveOrgSlug ? { org_slugs: [effectiveOrgSlug] } : {}),
          surface: 'trainer',
          register: false,
          call_mode: 'callcheck',
          ...(captchaToken ? { captcha_token: captchaToken } : {})
        })
      );
      setCallcheckPhonePretty(res.call_phone_pretty || res.call_phone || '');
      setCallStep('callcheck');
      setCheckId(res.check_id);
      savePendingCall({
        checkId: res.check_id,
        callPhonePretty: res.call_phone_pretty || res.call_phone || '',
        userPhone: callPhoneInput,
        orgSlug: effectiveOrgSlug,
        surface: 'trainer',
        createdAt: Date.now()
      });
      setCallCooldown(CALL_RESEND_COOLDOWN_S);
    } catch (err) {
      if (err instanceof ApiError) {
        setFormError(err.detail);
      } else {
        setFormError(err instanceof Error ? err.message : 'Ошибка запроса звонка');
      }
    } finally {
      setCallSubmitting(false);
      if (captcha.enabled) captcha.reset();
    }
  };

  const onCallSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    const phoneErr = validatePhoneRu(callPhoneInput, { required: true });
    if (phoneErr !== true) {
      setFormError(phoneErr);
      return;
    }
    const phoneNorm = normalizePhoneRu(callPhoneInput);
    lastPhoneNormRef.current = phoneNorm;
    await submitRequestCall(phoneNorm);
  };

  const onPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    const email = loginEmail.trim().toLowerCase();
    // Правило auth-methods-uniform §8: вход по паролю — email + телефон + пароль (3 поля).
    const errs: { email?: string; phone?: string; password?: string } = {};
    const emailErr = validateEmail(loginEmail, { required: true });
    if (emailErr !== true) errs.email = emailErr;
    const phoneErr = validatePhoneRu(accountPhone, { required: true });
    if (phoneErr !== true) errs.phone = phoneErr;
    if (!password.trim()) errs.password = 'Укажите пароль.';
    if (errs.email || errs.phone || errs.password) {
      setFieldErrors(errs);
      return;
    }
    setFieldErrors({});
    const phoneNorm = normalizePhoneRu(accountPhone);
    setPasswordLoading(true);
    try {
      const res = await captcha.run((captchaToken) =>
        postStaffLogin({
          email,
          phone: phoneNorm,
          password,
          surface: 'trainer',
          ...(effectiveOrgSlug ? { org_slug: effectiveOrgSlug, org_slugs: [effectiveOrgSlug] } : {}),
          ...(captchaToken ? { captcha_token: captchaToken } : {})
        })
      );
      if (isContextRequired(res)) {
        showContextChoice(res);
        return;
      }
      await completeStaffLogin(res);
    } catch (e) {
      if (e instanceof ApiError) {
        if (e.errorType === 'two_factor_required') {
          setTempToken(e.tempToken ?? '');
          setTwoFARequired(true);
          setTotpRecoveryMode(false);
          setTotpCode('');
          setTotpError(null);
          setFormError(null);
        } else if (e.errorType === 'phone_not_verified') {
          setFormError('Сначала войдите по номеру телефона, чтобы подтвердить его.');
        } else if (e.errorType === 'email_not_verified') {
          // Гейт email: показываем форму повторной отправки письма.
          setEmailNotVerified(true);
          setResendNote('Необходимо подтвердить email.');
          setFormError(null);
        } else {
          setFormError(trainerLoginErrorMessage(e));
        }
      } else {
        setFormError(e instanceof Error ? e.message : 'Ошибка входа');
      }
    } finally {
      setPasswordLoading(false);
      if (captcha.enabled) captcha.reset();
    }
  };

  const onTotpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTotpError(null);
    const rawCode = totpRecoveryMode
      ? totpCode.replace(/[^A-Z0-9]/g, '')
      : totpCode.replace(/\D/g, '');
    if (totpRecoveryMode) {
      if (rawCode.length !== 10) {
        setTotpError('Введите резервный код из 10 букв и цифр (XXXXX-XXXXX).');
        return;
      }
    } else if (rawCode.length !== 6) {
      setTotpError('Введите 6-значный код из приложения.');
      return;
    }
    const code = totpRecoveryMode ? `${rawCode.slice(0, 5)}-${rawCode.slice(5)}` : rawCode;
    setTotpLoading(true);
    try {
      const tokens = await captcha.run((captchaToken) =>
        postAuthLogin2FA({
          temp_token: tempToken,
          code,
          ...(captchaToken ? { captcha_token: captchaToken } : {})
        })
      );
      if (isContextRequired(tokens)) {
        showContextChoice(tokens);
        return;
      }
      const ok = await completeStaffLogin(tokens);
      if (!ok) setTotpError('Не удалось войти. Попробуйте ещё раз.');
    } catch (err) {
      if (err instanceof ApiError) {
        setTotpError(
          err.status === 401
            ? 'Неверный или просроченный код. Попробуйте ещё раз.'
            : trainerLoginErrorMessage(err)
        );
      } else {
        setTotpError(err instanceof Error ? err.message : 'Не удалось проверить код.');
      }
    } finally {
      setTotpLoading(false);
      if (captcha.enabled) captcha.reset();
    }
  };

  const onResendVerificationEmail = async () => {
    if (resendBusy || resendCooldown > 0) return;
    const email = loginEmail.trim().toLowerCase();
    if (validateEmail(email, { required: true }) !== true) {
      setResendNote('Укажите корректный email.');
      return;
    }
    setResendBusy(true);
    try {
      await postPublicResendEmailVerification(email);
      setResendNote('Письмо отправлено. Проверьте почту и подтвердите email.');
      setResendCooldown(RESEND_COOLDOWN_S);
    } catch (e) {
      if (e instanceof ApiError && e.status === 429) {
        setResendNote('Слишком частые попытки. Попробуйте позже.');
        setResendCooldown(RESEND_COOLDOWN_S);
      } else {
        setResendNote(e instanceof Error ? e.message : 'Не удалось отправить письмо.');
      }
    } finally {
      setResendBusy(false);
    }
  };

  const tabBtnClass = (active: boolean) =>
    `min-h-11 rounded-sm px-2 py-2 text-[12px] sm:text-[13px] font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)] sm:min-h-0 sm:py-1.5 ${
      active ? 'bg-[var(--bg-soft)] text-[var(--text)]' : 'text-[var(--text-muted)] hover:text-[var(--text)]'
    }`;

  return (
    <div className="space-y-4 w-full min-w-0">
      <h1 className="text-xl font-semibold tracking-tight">Вход тренера</h1>
      {searchParams.get('role_changed') === '1' ? (
        <EmbedMessageBlock variant="error" size="field" className="w-full" role="alert" aria-live="assertive">
          Ваша роль изменилась, войдите заново.
        </EmbedMessageBlock>
      ) : null}
      {contextChoice ? (
        <ContextSelectScreen
          title="Войти как"
          subtitle="Выберите, кем войти в приложение тренера."
          contexts={contextChoice.contexts}
          variant="role"
          busyId={contextBusyId}
          error={contextError}
          onSelect={(context) => void onSelectContext(context)}
        />
      ) : twoFARequired ? (
        <form className="space-y-3" onSubmit={onTotpSubmit} noValidate>
          <p className="text-sm text-[var(--text-muted)]">
            {totpRecoveryMode
              ? 'Введите один из резервных кодов, которые выдали при включении 2FA.'
              : 'Введите 6-значный код из приложения для подтверждения входа.'}
          </p>
          <div>
            <label className="mb-1.5 block text-[13px] font-medium text-[var(--text)]" htmlFor="trainer-totp-code">
              {totpRecoveryMode ? 'Резервный код (XXXXX-XXXXX)' : 'Код подтверждения'}
            </label>
            <input
              id="trainer-totp-code"
              type="text"
              inputMode={totpRecoveryMode ? 'text' : 'numeric'}
              autoComplete={totpRecoveryMode ? 'off' : 'one-time-code'}
              maxLength={totpRecoveryMode ? 11 : 6}
              placeholder={totpRecoveryMode ? 'XXXXX-XXXXX' : '000000'}
              className={`${inputClass} text-center font-mono ${
                totpRecoveryMode ? 'text-base tracking-[0.12em]' : 'text-lg tracking-[0.3em]'
              }`}
              value={totpCode}
              onChange={(e) => {
                setTotpCode(
                  totpRecoveryMode
                    ? normalizeRecoveryCodeInput(e.target.value)
                    : e.target.value.replace(/\D/g, '')
                );
                setTotpError(null);
              }}
            />
            <button
              type="button"
              data-testid="trainer-totp-recovery-toggle"
              className="mt-1.5 text-[12px] text-[var(--text-muted)] underline underline-offset-2 hover:text-[var(--text)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]"
              onClick={() => {
                setTotpRecoveryMode((v) => !v);
                setTotpCode('');
                setTotpError(null);
              }}
            >
              {totpRecoveryMode ? 'Войти с кодом из приложения' : 'Войти с резервным кодом'}
            </button>
          </div>
          {totpError || formError ? (
            <EmbedMessageBlock variant="error" size="field" className="w-full" role="alert" aria-live="assertive">
              {totpError ?? formError}
            </EmbedMessageBlock>
          ) : null}
          <SmartCaptchaNotice
            serverEnabled={captcha.serverEnabled}
            challengeVisible={captcha.challengeVisible}
            status={captcha.status}
            onRetry={captcha.refresh}
          />
          <Button
            type="submit"
            className="w-full min-h-11 sm:min-h-9"
            disabled={
              totpLoading ||
              captcha.loading ||
              (totpRecoveryMode
                ? totpCode.replace(/[^A-Z0-9]/g, '').length !== 10
                : totpCode.length !== 6)
            }
          >
            {totpLoading ? 'Проверяем…' : 'Подтвердить'}
          </Button>
          <Button
            type="button"
            variant="secondary"
            className="w-full"
            disabled={totpLoading}
            onClick={() => {
              setTwoFARequired(false);
              setTempToken('');
              setTotpCode('');
              setTotpRecoveryMode(false);
              setTotpError(null);
            }}
          >
            Назад
          </Button>
        </form>
      ) : (
        <>
      <p className="text-sm text-[var(--text-muted)]">
        {effectiveOrgSlug ? (
          <>
            Выберите способ входа. Организация:{' '}
            <span className="text-[var(--text)]">{effectiveOrgSlug}</span>
          </>
        ) : (
          'Войдите, чтобы увидеть расписание и своих клиентов.'
        )}
      </p>

      <div className="grid grid-cols-2 gap-1.5 sm:gap-2" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={authTab === 'call'}
          className={tabBtnClass(authTab === 'call')}
          onClick={() => switchTab('call')}
        >
          По звонку
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={authTab === 'password'}
          className={tabBtnClass(authTab === 'password')}
          onClick={() => switchTab('password')}
        >
          По паролю
        </button>
      </div>

      {authTab === 'call' ? (
        <div className="space-y-3">
          {callStep === 'phone' ? (
            <form className="space-y-3" onSubmit={onCallSubmit} noValidate>
              <div>
                <label className="mb-1 block text-[13px] font-medium text-[var(--text)]" htmlFor="trainer-call-phone">
                  Телефон
                </label>
                <EmbedPhoneInput
                  id="trainer-call-phone"
                  className={inputClass}
                  value={callPhoneInput}
                  onChange={setCallPhoneInput}
                />
              </div>
              {formError ? (
                <EmbedMessageBlock variant="error" size="field" className="w-full" role="alert" aria-live="assertive">
                  {formError}
                </EmbedMessageBlock>
              ) : null}
              <SmartCaptchaNotice
                serverEnabled={captcha.serverEnabled}
                challengeVisible={captcha.challengeVisible}
                status={captcha.status}
                onRetry={captcha.refresh}
              />
              <Button type="submit" className="w-full min-h-11 sm:min-h-9" disabled={!canSubmitCall || callSubmitting || captcha.loading}>
                {callSubmitting ? 'Запрашиваем…' : 'Позвонить для входа'}
              </Button>
            </form>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-[var(--text-muted)]">
                Позвоните на номер{' '}
                <a href={telHref(callcheckPhonePretty)} className="font-semibold text-[var(--text)] underline underline-offset-2">
                  {callcheckPhonePretty}
                </a>{' '}
                с вашего телефона <strong className="text-[var(--text)]">{callPhoneInput}</strong> — звонок будет сброшен, вход подтвердится автоматически.
              </p>
              <div className="rounded-xl border border-[var(--border)] bg-[var(--bg-soft)] p-4 text-center">
                <a
                  href={telHref(callcheckPhonePretty)}
                  className="text-2xl font-bold tracking-wider text-[var(--text)] hover:underline"
                >
                  {callcheckPhonePretty}
                </a>
                <p className="mt-1 mb-0 text-xs text-[var(--text-muted)]">
                  Оператор: SMS.ru
                </p>
              </div>
              <p className="text-sm text-[var(--text-muted)]">
                Звонок бесплатный. Как только вы совершите вызов, он будет сброшен, и вход выполнится автоматически.
              </p>
              <p className="text-xs text-[var(--text-muted)]">
                Можно свернуть приложение или вернуться в него позже: ожидание звонка сохранится, и вход продолжится автоматически.
              </p>
              {formError ? (
                <EmbedMessageBlock variant="error" size="field" className="w-full" role="alert" aria-live="assertive">
                  {formError}
                </EmbedMessageBlock>
              ) : null}
              <Button
                type="button"
                variant="secondary"
                className="w-full"
                onClick={() => {
                  setCallStep('phone');
                  setCheckId(null);
                  clearPendingCall('trainer');
                  setCallcheckPhonePretty('');
                  setFormError(null);
                  setCallCooldown(0);
                }}
              >
                Изменить номер
              </Button>
            </div>
          )}
        </div>
      ) : (
        <form className="space-y-3" onSubmit={onPasswordSubmit} noValidate>
          <div>
            <label className="mb-1.5 block text-[13px] font-medium text-[var(--text)]" htmlFor="trainer-login-email">
              Email *
            </label>
            <input
              id="trainer-login-email"
              type="email"
              autoComplete="username"
              placeholder="user@example.com"
              aria-invalid={fieldErrors.email ? true : undefined}
              className={`${inputClass}${fieldErrors.email ? ` ${embedFieldErrorInputClass}` : ''}`}
              value={loginEmail}
              onChange={(e) => {
                setLoginEmail(e.target.value);
                setFieldErrors((p) => ({ ...p, email: undefined }));
              }}
            />
            {fieldErrors.email ? <p className="mt-1 text-xs text-[var(--error-text)]">{fieldErrors.email}</p> : null}
          </div>
          <div>
            <label className="mb-1.5 block text-[13px] font-medium text-[var(--text)]" htmlFor="trainer-account-phone">
              Телефон *
            </label>
            <EmbedPhoneInput
              id="trainer-account-phone"
              className={`${inputClass}${fieldErrors.phone ? ` ${embedFieldErrorInputClass}` : ''}`}
              value={accountPhone}
              onChange={(v) => {
                setAccountPhone(v);
                setFieldErrors((p) => ({ ...p, phone: undefined }));
              }}
            />
            {fieldErrors.phone ? (
              <p className="mt-1 text-xs text-[var(--error-text)]">{fieldErrors.phone}</p>
            ) : (
              <p className="mt-1.5 text-xs leading-snug text-[var(--text-muted)]">
                Номер должен совпадать с телефоном учётной записи.
              </p>
            )}
          </div>
          <div>
            <label className="mb-1.5 block text-[13px] font-medium text-[var(--text)]" htmlFor="trainer-password">
              Пароль *
            </label>
            <div className="relative">
              <input
                id="trainer-password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                aria-invalid={fieldErrors.password ? true : undefined}
                className={`${inputClass} pr-11 sm:pr-10${fieldErrors.password ? ` ${embedFieldErrorInputClass}` : ''}`}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setFieldErrors((p) => ({ ...p, password: undefined }));
                }}
              />
              <button
                type="button"
                className="absolute right-1 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-sm text-[var(--text-muted)] hover:bg-[var(--bg-soft)] hover:text-[var(--text)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]"
                aria-label={showPassword ? 'Скрыть пароль' : 'Показать пароль'}
                aria-pressed={showPassword}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => setShowPassword((v) => !v)}
              >
                {showPassword ? <IconEyeOff /> : <IconEye />}
              </button>
            </div>
            <p className="mt-1.5 text-xs leading-snug text-[var(--text-muted)]">Пароль от аккаунта сотрудника.</p>
            {fieldErrors.password ? (
              <p className="mt-1 text-xs text-[var(--error-text)]">{fieldErrors.password}</p>
            ) : null}
          </div>
          {formError ? (
            <EmbedMessageBlock variant="error" size="field" className="w-full" role="alert" aria-live="assertive">
              {formError}
            </EmbedMessageBlock>
          ) : null}
          {emailNotVerified ? (
            <div className="space-y-2 rounded-xl bg-[var(--bg-soft)] p-3">
              <p className="text-sm text-[var(--text)]">
                Необходимо подтвердить email. Мы отправили письмо со ссылкой на{' '}
                <span className="font-medium">{loginEmail.trim().toLowerCase()}</span>. Проверьте почту, затем войдите по паролю.
              </p>
              {resendNote ? (
                <p className="text-xs text-[var(--text-muted)]" role="status" aria-live="polite">
                  {resendNote}
                </p>
              ) : null}
              <Button
                type="button"
                variant="secondary"
                className="w-full min-h-10 sm:min-h-8"
                disabled={resendBusy || resendCooldown > 0}
                onClick={() => void onResendVerificationEmail()}
              >
                {resendBusy
                  ? 'Отправляем…'
                  : resendCooldown > 0
                    ? `Отправить письмо повторно через ${resendCooldown} сек`
                    : 'Отправить письмо повторно'}
              </Button>
            </div>
          ) : null}
          <p className="m-0 text-xs text-[var(--muted)]">Для входа укажите email, телефон и пароль.</p>
          <SmartCaptchaNotice
            serverEnabled={captcha.serverEnabled}
            challengeVisible={captcha.challengeVisible}
            status={captcha.status}
            onRetry={captcha.refresh}
          />
          <Button type="submit" className="w-full min-h-11 sm:min-h-9" disabled={passwordLoading || captcha.loading}>
            {passwordLoading ? 'Входим…' : 'Войти'}
          </Button>
        </form>
      )}
        </>
      )}
    </div>
  );
}
